import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { changeMyPassword } from "../api/api";
import { useAuth } from "../context/AuthContext";
import { getRoleHomePage } from "../utils/roleHome";

function ChangePassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const { role, refreshUser } = useAuth();

  // Reached either by a forced redirect right after login (must_change_password
  // still true — nothing to navigate "back" to) or voluntarily from within the
  // app (e.g. a profile page's "Change Password" link, which can pass
  // { forced: false } via navigation state to show the Cancel button).
  const forced = location.state?.forced !== false;

  // Pre-filled from the login form when we got here via a forced redirect
  // right after login, so the user isn't asked to retype what they just
  // typed seconds ago — still shown/editable, just not blank. Falls back
  // to empty on a page refresh or direct visit, where this state is gone.
  const [currentPassword, setCurrentPassword] = useState(location.state?.tempPassword || "");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      await changeMyPassword(currentPassword, newPassword);
      const updatedUser = await refreshUser();
      navigate(getRoleHomePage(updatedUser?.role?.name || role));
    } catch (err) {
      const status = err.response?.status;
      if (status === 400) {
        setError("Current password is incorrect.");
      } else if (status === 422) {
        setError("Please check the form — some fields are invalid.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-page">
      <form className="login-box" onSubmit={handleSubmit}>
        <h1>🌿 Change Password</h1>

        <p className="login-subtitle">
          {forced
            ? "For your security, please set a new password before continuing."
            : "Choose a new password for your account."}
        </p>

        {error && (
          <p className="login-error" style={{ color: "red" }}>
            {error}
          </p>
        )}

        <div className="password-field">
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Current (temporary) password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
          <button
            type="button"
            className="password-toggle-btn"
            onClick={() => setShowPassword((prev) => !prev)}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <FaEyeSlash /> : <FaEye />}
          </button>
        </div>

        <div className="password-field">
          <input
            type={showPassword ? "text" : "password"}
            placeholder="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </div>

        <div className="password-field">
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </div>

        <button type="submit" className="login-btn" disabled={submitting}>
          {submitting ? "Changing…" : "Change Password"}
        </button>

        {!forced && (
          <button
            type="button"
            className="register-btn"
            onClick={() => navigate(-1)}
          >
            Cancel
          </button>
        )}
      </form>
    </div>
  );
}

export default ChangePassword;
